import type { LucideIcon } from "lucide-react";
import { Landmark, TrainFront } from "lucide-react";
import { DataKeys, DataStructure } from "../../types";
import { useLang } from "../Context";
import { MainFrame, SpacingFrame } from "../display/Frame";
import { TitleSubtitle, TextBody, TextBulletList } from "../display/TextFormat";
import { StaggerInView, StaggerInViewItem } from "../tools/Animations";
import {
    HEALTH_BG_GREY,
    healthWhyDoctorCard,
    healthWhyDoctorCardBody,
    healthWhyDoctorCardContent,
    healthWhyDoctorCardDivider,
    healthWhyDoctorCardIconCol,
    healthWhyDoctorCardTitle,
    healthWhyDoctorGrid,
    healthWhyDoctorIconGlyph,
    healthWhyDoctorIconShell,
} from "../health/healthVisualStyles";

const VENUE_ORDER = ["attractions", "transportation"] as const;
const VENUE_ICONS: Record<(typeof VENUE_ORDER)[number], LucideIcon> = {
    attractions: Landmark,
    transportation: TrainFront,
};
const VENUE_ICON_STROKE = 1.75;

interface AgentsVenueOverviewProps {
    sectionData: DataKeys;
}

export function AgentsVenueOverview({ sectionData }: AgentsVenueOverviewProps) {
    const { lang } = useLang();
    const venues = sectionData["venues"] as DataStructure;
    const sectionTitle = sectionData["title"]?.[lang] ?? "";

    return (
        <MainFrame style={{ backgroundColor: HEALTH_BG_GREY }} smallerPadding>
            <SpacingFrame spacing={40}>
                {sectionTitle ? <TitleSubtitle title={sectionTitle} /> : null}
                <StaggerInView className={healthWhyDoctorGrid}>
                    {VENUE_ORDER.map((id) => {
                        const venue = venues[id] as DataKeys;
                        if (!venue) return null;

                        const Icon = VENUE_ICONS[id];
                        const title = venue["title"]?.[lang] ?? "";
                        const text = venue["text"]?.[lang] ?? "";
                        const bullets =
                            (venue["bullets"]?.[lang] as string[]) ?? [];

                        return (
                            <StaggerInViewItem key={id} direction="bottom">
                                <article className={healthWhyDoctorCard}>
                                    <div className={healthWhyDoctorCardIconCol}>
                                        <div className={healthWhyDoctorIconShell}>
                                            <Icon
                                                className={healthWhyDoctorIconGlyph}
                                                strokeWidth={VENUE_ICON_STROKE}
                                                aria-hidden
                                            />
                                        </div>
                                    </div>
                                    <div
                                        className={healthWhyDoctorCardDivider}
                                        aria-hidden
                                    />
                                    <div className={healthWhyDoctorCardContent}>
                                        <h3 className={healthWhyDoctorCardTitle}>
                                            {title}
                                        </h3>
                                        {text ? (
                                            <p className={healthWhyDoctorCardBody}>
                                                {text}
                                            </p>
                                        ) : null}
                                        {bullets.length > 0 ? (
                                            <div className="mt-4 border-t border-grey-100/90 pt-4">
                                                <TextBulletList list={bullets} />
                                            </div>
                                        ) : null}
                                    </div>
                                </article>
                            </StaggerInViewItem>
                        );
                    })}
                </StaggerInView>
            </SpacingFrame>
        </MainFrame>
    );
}
